import { Prec } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

import { insertPastedContent, pastedContent, storePastedContent } from "./pasted-content.js";
import {
    isBlockContainingElement,
    isBlockElement,
    isElementNode,
    isInlineElement,
    isTextNode,
    shouldRemoveEmptyBlockElement,
} from "./html-elements.js";

const specialCharacterRegExp = /(^|[^\\])([#$%&~_^\\{}])/g;

const specialCharacterReplacer = (_match, prefix, char) => {
    if (char === "\\") {
        char = "textbackslash{}";
    }

    return `${prefix}\\${char}`;
};

const headingCommands = ["section", "subsection", "subsubsection", "paragraph", "subparagraph", "subparagraph"];

function hasProgId(documentElement) {
    const meta = documentElement.querySelector('meta[name="ProgId"]');
    return meta && meta.content.trim().length > 0;
}

function isOnlyTable(documentElement) {
    const body = documentElement.querySelector("body");
    return body && body.childElementCount === 1 && body.firstElementChild?.nodeName === "TABLE";
}

function findCodeContainingElement(documentElement) {
    let result = documentElement.querySelector("code");
    if (result) {
        return result;
    }

    result = documentElement.querySelector("pre");
    if (result?.style?.fontFamily?.includes("monospace")) {
        return result;
    }

    return null;
}

function onlyCode(documentElement) {
    const codeElement = findCodeContainingElement(documentElement);
    return codeElement?.textContent?.trim() === documentElement.textContent?.trim();
}

function isElementContainingCode(node) {
    if (!isElementNode(node)) {
        return false;
    }

    return node.nodeName === "CODE" || node.nodeName === "PRE" || node.nodeName === "TEXTAREA";
}

function removeUnwantedElements(documentElement, selector) {
    for (const element of documentElement.querySelectorAll(selector)) {
        element.remove();
    }
}

function normaliseWhitespace(documentElement) {
    const walker = document.createTreeWalker(documentElement, NodeFilter.SHOW_TEXT, (node) =>
        isElementNode(node) && isElementContainingCode(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
    );

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const text = node.textContent;
        if (text !== null) {
            if (/^\s+$/.test(text)) {
                node.textContent = " ";
            } else {
                node.textContent = text.replaceAll(/[\n\r\f\t \u2028\u2029]+/g, " ");
            }
        }
    }
}

function trimInlineElements(element, precedingSpace = true) {
    for (const node of [...element.childNodes]) {
        if (isTextNode(node)) {
            let text = node.textContent;

            if (precedingSpace) {
                text = text.replace(/^\s+/, "");
            }

            if (text === "") {
                node.remove();
            } else {
                node.textContent = text;
                precedingSpace = /\s$/.test(text);
            }
        } else if (isElementNode(node) && isInlineElement(node)) {
            precedingSpace = trimInlineElements(node, precedingSpace);
        } else if (isElementNode(node) && isBlockElement(node)) {
            precedingSpace = true;
        } else {
            precedingSpace = false;
        }
    }

    return precedingSpace;
}

function processWhitespaceInBlocks(documentElement) {
    trimInlineElements(documentElement);

    const walker = document.createTreeWalker(documentElement, NodeFilter.SHOW_ELEMENT, (node) =>
        isElementNode(node) && isElementContainingCode(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
    );

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (!isElementNode(node)) {
            continue;
        }

        if (isBlockContainingElement(node)) {
            for (const childNode of [...node.childNodes]) {
                if (isTextNode(childNode)) {
                    childNode.remove();
                }
            }
        }

        if (isBlockElement(node)) {
            trimInlineElements(node);

            if (shouldRemoveEmptyBlockElement(node)) {
                node.remove();
            }
        }
    }
}

function protectSpecialCharacters(documentElement) {
    const walker = document.createTreeWalker(documentElement, NodeFilter.SHOW_TEXT, (node) =>
        isElementNode(node) && isElementContainingCode(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
    );

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (!isTextNode(node)) {
            continue;
        }
        const text = node.textContent;
        if (text) {
            node.textContent = text.replaceAll(specialCharacterRegExp, specialCharacterReplacer);
        }
    }
}

function convertInline(node) {
    if (isTextNode(node)) {
        return node.textContent || "";
    }

    if (!isElementNode(node)) {
        return "";
    }

    const content = [...node.childNodes].map(convertInline).join("");
    switch (node.nodeName) {
        case "STRONG":
        case "B":
            return `\\textbf{${content}}`;
        case "EM":
        case "I":
            return `\\textit{${content}}`;
        case "SUB":
            return `\\textsubscript{${content}}`;
        case "SUP":
            return `\\textsuperscript{${content}}`;
        case "A": {
            const href = node.getAttribute("href") || "";
            return href ? `\\href{${href}}{${content || href}}` : content;
        }
        case "CODE":
            return `\\texttt{${content}}`;
        case "BR":
            return "\\\\";
        default:
            return content;
    }
}

function convertTable(table) {
    const rows = [...table.querySelectorAll("tr")];
    if (rows.length === 0) {
        return "";
    }

    const body = rows
        .map((row) =>
            [...row.children]
                .filter((cell) => ["TD", "TH"].includes(cell.nodeName))
                .map((cell) => convertInline(cell).trim() || " ")
                .join(" & "),
        )
        .filter(Boolean);

    const cols = Math.max(...body.map((row) => row.split("&").length), 1);
    const spec = "l".repeat(cols);

    return [
        "\\begin{table}",
        "\\centering",
        `\\begin{tabular}{${spec}}`,
        ...body.map((row, index) => `${row} \\\\${index === 0 ? "" : ""}`),
        "\\end{tabular}",
        "\\end{table}",
    ].join("\n");
}

function convertList(list, ordered = false) {
    const environment = ordered ? "enumerate" : "itemize";
    const items = [...list.children]
        .filter((child) => child.nodeName === "LI")
        .map((item) => {
            const nested = [...item.children].find((child) => child.nodeName === "UL" || child.nodeName === "OL");
            const inlineNodes = [...item.childNodes].filter((child) => child !== nested);
            const content = inlineNodes.map(convertInline).join("").trim();
            const nestedLatex = nested ? `\n${convertBlock(nested)}` : "";
            return `\\item ${content}${nestedLatex}`;
        })
        .join("\n");

    return `\\begin{${environment}}\n${items}\n\\end{${environment}}`;
}

function convertBlock(node, depth = 0) {
    if (isTextNode(node)) {
        return (node.textContent || "").trim();
    }

    if (!isElementNode(node)) {
        return "";
    }

    switch (node.nodeName) {
        case "P":
        case "DIV":
        case "SECTION":
        case "ARTICLE":
            return [...node.childNodes]
                .map((child) => convertBlock(child, depth))
                .filter(Boolean)
                .join("\n\n");
        case "H1":
        case "H2":
        case "H3":
        case "H4":
        case "H5":
        case "H6": {
            const level = Math.max(0, Math.min(headingCommands.length - 1, Number(node.nodeName.slice(1)) - 1));
            return `\\${headingCommands[level]}{${convertInline(node).trim()}}`;
        }
        case "UL":
            return convertList(node, false);
        case "OL":
            return convertList(node, true);
        case "TABLE":
            return convertTable(node);
        case "BLOCKQUOTE":
            return `\\begin{quote}\n${[...node.childNodes].map((child) => convertBlock(child, depth + 1)).join("\n\n")}\n\\end{quote}`;
        case "PRE":
            return `\\begin{verbatim}\n${node.textContent || ""}\n\\end{verbatim}`;
        case "BR":
            return "\\\\";
        default:
            return convertInline(node).trim();
    }
}

export function convertHtmlStringToLatex(html, filesLength = 0) {
    const parser = new DOMParser();
    const documentNode = parser.parseFromString(html, "text/html");
    const { documentElement } = documentNode;

    if (!documentElement) {
        return null;
    }

    if (filesLength > 0 && !hasProgId(documentElement) && !isOnlyTable(documentElement)) {
        return null;
    }

    const body = documentNode.body;
    if (!body) {
        return null;
    }

    if (onlyCode(body)) {
        return null;
    }

    removeUnwantedElements(body, "style");

    let before = null;
    let after = null;

    do {
        before = body.textContent;
        normaliseWhitespace(body);
        processWhitespaceInBlocks(body);
        after = body.textContent;
    } while (before !== after);

    protectSpecialCharacters(body);

    const text = [...body.childNodes]
        .map((child) => convertBlock(child))
        .filter(Boolean)
        .join("\n\n")
        .replaceAll("​", "")
        .replaceAll(/\n{3,}/g, "\n\n")
        .replaceAll(/(^\n+|\n+$)/g, "\n")
        .replaceAll("\t", "    ");

    return text;
}

export const pasteHtml = [
    Prec.highest(
        EditorView.domEventHandlers({
            paste(event, view) {
                const { clipboardData } = event;
                if (!clipboardData) {
                    return false;
                }

                if (!clipboardData.types.includes("text/html")) {
                    return false;
                }

                if (
                    clipboardData.types.includes("application/vnd.code.copymetadata") ||
                    clipboardData.types.includes("vscode-editor-data")
                ) {
                    return false;
                }

                const html = clipboardData.getData("text/html").trim();
                const text = clipboardData.getData("text/plain").trim();
                if (!html) {
                    return false;
                }

                const latex = convertHtmlStringToLatex(html, clipboardData.files.length);
                if (latex == null || latex === text) {
                    return false;
                }

                event.preventDefault();
                view.dispatch(insertPastedContent(view, { latex, text }));
                view.dispatch(storePastedContent({ latex, text }, true));
                return true;
            },
        }),
    ),
    pastedContent,
];
